// These are web-only components that can use the theme-ui theme and sx prop
/** @jsx jsx */
import { jsx } from '@theme-ui/core';
import { memo, forwardRef } from 'react';
import { NavLink } from 'react-router-dom';
import { layout } from 'app/styles';
import TextareaAutosize from 'react-textarea-autosize';
import ReactTextareaAutocomplete from '@webscopeio/react-textarea-autocomplete';
import { theme } from 'styles/theme';
import {
  BoxProps,
  ButtonProps,
  LinkProps,
  InputProps,
  ImageProps,
  EmbedProps,
} from '@theme-ui/components';

export const StyledNavLink = memo<BoxProps>(({ sx, children, ...props }) => (
  <NavLink
    {...props}
    sx={{
      div: {
        color: 'grey',
      },
      '&.active div': {
        color: 'black !important',
      },
      ':hover div': {
        color: 'black !important',
      },
      margin: 0,
      ...sx,
    }}
  >
    {children}
  </NavLink>
));

export const Button = memo<ButtonProps>(({ sx, disabled, ...props }) => (
  <button
    {...props}
    sx={{
      ...layout.button,
      '&:hover': layout.activeButtonShadow,
      bg: disabled ? 'grey' : 'blue',
      userSelect: 'none',
      cursor: 'pointer',
      ...sx,
    }}
  />
));

export const ButtonAnchor = memo<LinkProps & ButtonProps>(
  ({ sx, disabled, children, ...props }) => (
    <a
      {...props}
      sx={{
        ...layout.button,
        '&:hover': layout.activeButtonShadow,
        bg: disabled ? 'grey' : 'blue',
        userSelect: 'none',
        cursor: 'pointer',
        ...sx,
      }}
    >
      {children}
    </a>
  )
);

export const InputPlain = forwardRef<HTMLInputElement, InputProps>(
  ({ sx, ...props }, ref) => (
    <input
      ref={ref}
      {...props}
      sx={{
        bg: 'white',
        '::placeholder': {
          fontSize: 2,
          lineHeight: 2,
        },
        variant: 'borders.input',
        ...sx,
      }}
    />
  )
);

export const Input = forwardRef<HTMLInputElement, InputProps>(({ sx, ...props }, ref) => (
  <input
    {...props}
    ref={ref}
    sx={{
      bg: 'white',
      ...theme.text.body,
      '::placeholder': {
        fontSize: 2,
        lineHeight: 2,
      },
      variant: 'borders.input',
      lineHeight: '21px',
      padding: 2,
      mt: 1,
      ...sx,
    }}
  />
));

export const StyledTextareaAutocomplete = forwardRef<HTMLDivElement, BoxProps>(
  ({ sx, ...props }, ref) => (
    <ReactTextareaAutocomplete
      {...props}
      ref={ref}
      sx={{
        ...theme.text.body,
        lineHeight: '21px',
        padding: 2,
        mt: 1,
        variant: 'borders.input',
        ...sx,
      }}
    />
  )
);

export const StyledTextarea = forwardRef<HTMLDivElement, BoxProps>(
  ({ sx, ...props }, ref) => (
    <TextareaAutosize
      ref={ref}
      {...props}
      sx={{
        ...theme.text.body,
        lineHeight: '21px',
        padding: 2,
        variant: 'borders.input',
        ...sx,
      }}
    />
  )
);

export const Form = ({ sx, ...props }): JSX.Element => <form {...props} sx={sx} />;

export const Video = ({ sx, ...props }): JSX.Element => (
  // eslint-disable-next-line jsx-a11y/media-has-caption
  <video {...props} sx={sx} />
);

export const Image = ({ sx, ...props }: ImageProps): JSX.Element => (
  // eslint-disable-next-line jsx-a11y/alt-text
  <img {...props} sx={sx} />
);

export const View = memo(
  forwardRef<HTMLDivElement, BoxProps>(({ sx, onClick, ...props }, ref) => (
    // eslint-disable-next-line
    <div
      ref={ref}
      {...props}
      onClick={onClick}
      sx={{ display: 'flex', cursor: onClick ? 'pointer' : 'auto', ...sx }}
    />
  ))
);
